import React from 'react';
import { ExpandedType } from '../..';
import {
  AbsencePeriodDecisions,
  AccommodationCase,
} from '../../../../../data-models';
import {
  AccommodationCardList,
  CardHeaderSection,
  ClaimsCardItems,
  LeaveCardItems,
  PostIntakeLeaves,
  PostIntakeAccommodations,
  HelpSection,
  LeaveLogicCard,
} from '../../status-tracker/card-items';

import { SectionMappingProps, TabCardsNonOverview } from '../tab-cards';
import {
  useGetAbsencePeriodDecisions,
  useGetAccommodationCases,
} from '../../../../../hooks/use-data';
import { Spinner } from '../../../../../components/base/spinner';
import { useLDClient } from '@launchdarkly/react-sdk';

export const DetailsTab: ExpandedType = (props) => {
  const { claim, NTNId, EETasks, ERTasks, areTasksDue, hasLetters, status } =
    props;
  const leavesForNTN = claim?.leaves;
  const accommodationsForNTN = claim?.accommodations;
  const absenceNotifications = props.absenceNotifications;
  const ptoChoice = props.ptoChoice;

  const ldClient = useLDClient();

  const caseIds =
    leavesForNTN
      ?.map((leave) => leave.caseId)
      .filter((id): id is string => typeof id === 'string') || [];

  const accommodationCaseIds =
    accommodationsForNTN
      ?.map((accomms) => {
        if (typeof accomms.caseId === 'string') {
          return accomms.caseId;
        } else if (typeof accomms.accommodationId === 'string') {
          return accomms.accommodationId;
        }
        return null;
      })
      .filter((id): id is string => typeof id === 'string') || [];

  const {
    data: absencePeriodDecisions,
    isFetching: isFetchingAbsence,
    isError: isErrorAbsence,
    error: absenceError,
  } = useGetAbsencePeriodDecisions(caseIds) as {
    data: AbsencePeriodDecisions[] | undefined;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
  };

  const {
    data: accommodationCase = [],
    isFetching: isFetchingAccommodation,
    isError: isErrorAccommodation,
    error: accommodationError,
  } = useGetAccommodationCases(accommodationCaseIds) as {
    data: AccommodationCase[] | undefined;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
  };

  const isLoading = isFetchingAbsence || isFetchingAccommodation;
  const isError = isErrorAbsence || isErrorAccommodation;

  const hasTAbsId = absencePeriodDecisions?.some((item) =>
    item.absenceId.startsWith('T ABS-'),
  );

  const hasAbsencePeriodDecisions = !!(
    absencePeriodDecisions && absencePeriodDecisions.length > 0
  );

  const hasLeavesForNTN = !!(leavesForNTN && leavesForNTN.length > 0);

  const hasAccommodations = !!(
    accommodationCase && accommodationCase.length > 0
  );

  const compileSectionMapping = React.useCallback(() => {
    const sectionMapping: SectionMappingProps['sectionMapping'] = {};

    if (isLoading) {
      sectionMapping.leaves = {
        title: 'Getting details...',
        headerLinkName: '',
        headerLink: '',
        popupContent: 'Loading, please wait...',
        component: (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-pb-4">
            <Spinner></Spinner>
          </div>
        ),
      };
    } else {
      if (isError) {
        sectionMapping.leaves = {
          title: 'Error retrieving data...',
          headerLinkName: '',
          headerLink: '',
          popupContent: 'Please try again later.',
          component: (
            <div>
              Error loading leave coverages:{' '}
              {String(absenceError || accommodationError)}
            </div>
          ),
        };
      }

      if (!hasLetters && !hasTAbsId && hasLeavesForNTN) {
        sectionMapping.leaves = {
          title: 'Leave Coverages',
          headerLinkName: 'Go to available time',
          headerLink: '/app/time',
          popupContent:
            'Leave coverages could protect your job and/or income while you are out of work. You may be eligible for federal, state, or corporate leaves. Please click ‘View Calendar’ and review your letter(s) in the overview section for more information about your leave coverages.',
          component: (
            <div className="tw-pb-2">
              <PostIntakeLeaves
                leaves={leavesForNTN}
                absenceNotifications={absenceNotifications}
                ptoChoice={ptoChoice}
                statusTracker={status}
              />
            </div>
          ),
        };
      }

      if (hasAbsencePeriodDecisions && (hasLetters || hasTAbsId)) {
        sectionMapping.leaves = {
          title: 'Leave Coverages',
          headerLinkName: 'Go to available time',
          headerLink: '/app/time',
          popupContent:
            'Leave coverages could protect your job and/or income while you are out of work. You may be eligible for federal, state, or corporate leaves. Please click ‘View Calendar’ and review your letter(s) in the overview section for more information about your leave coverages.',
          component: (
            <div className="tw-pb-2">
              <LeaveCardItems
                leaves={leavesForNTN}
                absencePeriodDecisions={absencePeriodDecisions}
              />
              {/* <LeaveCardDataTest></LeaveCardDataTest> */}
            </div>
          ),
        };
      }

      if (claim?.claims?.length > 0 && hasLetters) {
        sectionMapping.claims = {
          title: 'Disability Benefits',
          headerLinkName: '',
          headerLink: '',
          popupContent:
            'Disability benefits may replace a portion of your income due to injury or illness. You may be eligible for disability through your employer or your state, depending on where you reside or work. Please click ‘View Calendar’ and review your letter(s) in the overview section for more information about your disability benefits.',
          component: (
            <div className="tw-pb-2">
              <ClaimsCardItems
                claims={claim.claims}
                ERTasks={ERTasks}
                EETasks={EETasks}
              />
            </div>
          ),
        };
      }

      if (hasAccommodations && hasLetters) {
        sectionMapping.accommodations = {
          title: 'Accommodations',
          headerLinkName: '',
          headerLink: '',
          popupContent:
            'Accommodations are covered under the Americans with Disabilities Act (ADA). Please review your letter(s) in the overview section for more information about your accommodations.',
          component: (
            <div className="tw-pb-2">
              <AccommodationCardList cases={accommodationCase} />
            </div>
          ),
        };
      } else if (hasAccommodations && !hasLetters) {
        sectionMapping.leaves = {
          title: 'Accomodations',
          headerLinkName: 'Go to available time',
          headerLink: '/app/time',
          popupContent:
            'Accommodations are covered under the Americans with Disabilities Act (ADA). Please review your letter(s) in the overview section for more information about your accommodations.',
          component: (
            <div className="tw-pb-2">
              <PostIntakeAccommodations cases={accommodationCase} />
            </div>
          ),
        };
      }
    }

    return sectionMapping;
  }, [
    EETasks,
    ERTasks,
    claim.claims,
    absencePeriodDecisions,
    isLoading,
    isError,
    absenceError,
    accommodationError,
    hasLetters,
    leavesForNTN,
    hasTAbsId,
    hasAbsencePeriodDecisions,
    accommodationCase,
    hasAccommodations,
    hasLeavesForNTN,
    absenceNotifications,
    ptoChoice,
    status,
  ]);

  return (
    <>
      <CardHeaderSection {...claim} NTNId={NTNId} areTasksDue={areTasksDue} />
      <TabCardsNonOverview
        sectionMapping={compileSectionMapping()}
        isDetailsTab={true}
        hasLetters={hasLetters}
        hasTAbsId={hasTAbsId}
      />
      {ldClient?.boolVariation('enable-ll-redirect', false) && (
        <LeaveLogicCard ntn={NTNId} />
      )}
      {!hasLetters && <HelpSection></HelpSection>}
    </>
  );
};
